/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Header, Icon, Segment } from '@mairistem/composants';

import EnteteApercu, { HAUTEUR_ENTETE_APERCU } from './EnteteApercu';

interface ApercuDocxProps {
  /** Octets du document, tels que décodés par l'aperçu. */
  binaire?: Uint8Array;
  /** Titre du bandeau, aligné sur celui du canevas. */
  titre?: string;
  /** Page à faire défiler dans la vue, numérotée à partir de 1. */
  pageCourante?: number;
  /**
   * Pages rendues, remontées à l'écran pour alimenter la colonne des vignettes.
   *
   * `docx-preview` ne produit pas d'images : les pages n'existent que sous
   * forme de `<section>` dans le DOM, que la colonne réduit à l'échelle.
   */
  onSectionsChange?: (sections: Array<HTMLElement>) => void;
}

/**
 * Aperçu d'un document DOCX.
 *
 * Rendu dans le navigateur par `docx-preview`, sans conversion serveur : la
 * quasi-totalité des documents bureautiques de la base sont des DOCX, même
 * ceux déclarés `doc`.
 *
 * `docx-preview` écrit directement dans un élément du DOM plutôt que de
 * produire des images — d'où ce composant distinct du canevas, comme pour le
 * RTF.
 */
const ApercuDocx = (props: ApercuDocxProps) => {
  const {
    binaire,
    titre = 'APERÇU DU DOCUMENT',
    pageCourante = 1,
    onSectionsChange,
  } = props;

  const defilement = React.useRef<HTMLDivElement>(null);
  const conteneur = React.useRef<HTMLDivElement>(null);
  const [erreur, setErreur] = React.useState(false);
  const [sections, setSections] = React.useState<Array<HTMLElement>>([]);

  React.useEffect(() => {
    if (!binaire || !conteneur.current) return undefined;

    let vivant = true;
    const cible = conteneur.current;
    cible.innerHTML = '';
    setErreur(false);

    // Import différé : la bibliothèque ne sert qu'à ce format, inutile de
    // l'embarquer dans le chargement initial de l'application.
    import('docx-preview')
      .then(({ renderAsync }) =>
        renderAsync(binaire, cible, undefined, {
          className: 'docx-apercu',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          // Découpe le rendu en une `<section>` par page, ce dont dépend la
          // colonne des vignettes.
          breakPages: true,
          experimental: true,
        }),
      )
      .then(() => {
        if (!vivant) return;
        setSections(Array.from(cible.querySelectorAll<HTMLElement>('section')));
      })
      .catch(() => {
        if (vivant) setErreur(true);
      });

    return () => {
      vivant = false;
    };
  }, [binaire]);

  React.useEffect(() => {
    onSectionsChange?.(sections);
  }, [sections, onSectionsChange]);

  // Défilement jusqu'à la page choisie dans la colonne des vignettes.
  React.useEffect(() => {
    const vue = defilement.current;
    const section = sections[pageCourante - 1];
    if (!vue || !section) return;

    /*
      `scrollIntoView` ferait aussi défiler la page entière : on se limite au
      conteneur, en reportant l'écart entre les deux positions à l'écran.
    */
    vue.scrollTop +=
      section.getBoundingClientRect().top - vue.getBoundingClientRect().top;
  }, [pageCourante, sections]);

  return (
    <>
      <EnteteApercu titre={titre} />
      <div
        ref={defilement}
        style={{
          height: `calc(100% - ${HAUTEUR_ENTETE_APERCU}px)`,
          overflow: 'auto',
          border: '1px solid #dee2e6',
          borderTop: 'none',
          backgroundColor: '#E5EDF5',
        }}
      >
        {erreur ? (
          <Segment placeholder basic>
            <Header icon>
              <Icon name="file word outline" />
              Ce document n’a pas pu être affiché.
            </Header>
          </Segment>
        ) : (
          <div ref={conteneur} data-testid="apercu-docx" />
        )}
      </div>
    </>
  );
};

export default ApercuDocx;
